import { SECONDS_PER_DAY, resolutionToLabel } from "../../constants";
import {
  CANDLE_BODY_WIDTH_RATIO,
  COUNTDOWN_BAR_GAP_PX,
  COUNTDOWN_EDGE_PADDING_PX,
  COUNTDOWN_LINE_GAP_PX,
  COUNTDOWN_LINE_HEIGHT_PX,
} from "../constants";

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const LINE_COUNT = 2;
const TIMER_HEIGHT_PX = LINE_COUNT * (COUNTDOWN_LINE_HEIGHT_PX + COUNTDOWN_LINE_GAP_PX);

const pad = (value) => String(value).padStart(2, "0");

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Formats remaining seconds as "mm:ss", "hh:mm:ss" or "Nd hh:mm".
 *
 * @param {number} totalSeconds
 * @returns {string}
 */
export const formatCountdown = (totalSeconds) => {
  const days = Math.floor(totalSeconds / SECONDS_PER_DAY);
  const hours = Math.floor((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;

  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}`;
  if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

  return `${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Bar timer right of the last bar (po-chart-visual-spec.md §12.5): the
 * timeframe above the last price, the time left until the bar closes below
 * it, right-aligned. Anchored at the time position of the last bar plus the
 * TimeScale phase: while the view follows real time it stands still and the
 * bars flow under it, a new bar does not move it; otherwise the phase is 0
 * and it is right of the last bar. Stays inside the pane, left of the price
 * axis.
 *
 * @param {object} params
 * @param {{ index: number, price: number }} params.lastValue - mainSeries.lastValue
 * @param {number} params.lastBarTime - open time of the last stored bar, ms
 * @param {number} params.barDurationMs
 * @param {string} params.resolution - TV resolution, e.g. "15S", "1", "60"
 * @param {object} params.timeScale
 * @param {object} params.priceScale
 * @param {{ width: number, height: number }} params.pane
 * @param {(text: string, font: string) => number} params.measure
 * @param {string[]} params.fonts - of the timeframe and of the countdown lines
 * @param {number} params.now - ms
 * @returns {{ rect: { x: number, y: number, width: number, height: number }, texts: string[] }}
 */
export const resolveCountdown = ({
  lastValue,
  lastBarTime,
  barDurationMs,
  resolution,
  timeScale,
  priceScale,
  pane,
  measure,
  fonts,
  now,
}) => {
  const secondsLeft = Math.max(0, Math.ceil((lastBarTime + barDurationMs - now) / MS_PER_SECOND));
  const texts = [resolutionToLabel(resolution), formatCountdown(secondsLeft)];
  const width = Math.max(...texts.map((text, line) => measure(text, fonts[line])));
  const bodyRight =
    timeScale.indexToX(lastValue.index + timeScale.phase) +
    (timeScale.barSpacing * CANDLE_BODY_WIDTH_RATIO) / 2;
  const x = clamp(
    bodyRight + COUNTDOWN_BAR_GAP_PX,
    0,
    pane.width - COUNTDOWN_EDGE_PADDING_PX - width,
  );
  const centreY = clamp(
    priceScale.priceToY(lastValue.price),
    TIMER_HEIGHT_PX / 2,
    pane.height - TIMER_HEIGHT_PX / 2,
  );

  return { rect: { x, y: centreY - TIMER_HEIGHT_PX / 2, width, height: TIMER_HEIGHT_PX }, texts };
};

/**
 * @param {object} textLayer
 * @param {{ rect: object, texts: string[] }} countdown - from resolveCountdown
 * @param {Array<{ font: string, color: string }>} lineStyles - timeframe, countdown
 */
export const drawCountdown = (textLayer, { rect, texts }, lineStyles) => {
  const lineStep = COUNTDOWN_LINE_HEIGHT_PX + 2 * COUNTDOWN_LINE_GAP_PX;

  texts.forEach((text, line) => {
    const centreY = rect.y + COUNTDOWN_LINE_HEIGHT_PX / 2 + line * lineStep;

    textLayer.drawText(text, rect.x + rect.width, centreY, { ...lineStyles[line], align: "right" });
  });
};
